import { useState, useMemo, useCallback, useEffect } from "react";
import { Button, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { List, useTable } from "@refinedev/antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useDelete, getDefaultFilter, useGo } from "@refinedev/core";
import dayjs from "dayjs";

const { Text } = Typography;

const API_URL = (import.meta as any).env?.VITE_API_URL || "http://localhost:8085";

interface StopWordCategory {
  id: string;
  label: string;
  color: string;
  action_type: string;
  description?: string;
}

type Template = {
  id: string;
  name: string;
  description: string;
  category: string;
  content: string;
  variables?: string[];
  recommended_sentiment?: string[];
  recommended_rating?: number[];
  use_case_note?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export default function TemplatesList() {
  const go = useGo();

  // Load categories from API (single source of truth)
  const [categories, setCategories] = useState<StopWordCategory[]>([]);

  useEffect(() => {
    fetch(`${API_URL}/api/v1/stop-word-categories`)
      .then(res => res.json())
      .then(data => setCategories(data.items || []))
      .catch(() => {});
  }, []);

  const categoryMap = useMemo(() => {
    const map: Record<string, { label: string; color: string }> = {};
    categories.forEach((cat) => {
      map[cat.id] = { label: cat.label, color: cat.color };
    });
    return map;
  }, [categories]);

  const categoryOptions = useMemo(() => {
    return categories.map((cat) => ({
      value: cat.id,
      label: cat.label,
    }));
  }, [categories]);

  const {
    tableProps,
    filters,
    setFilters,
    tableQuery,
  } = useTable<Template>({
    resource: "templates",
    pagination: { mode: "server", pageSize: 20 },
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
    filters: {
      initial: [
        { field: "name", operator: "contains", value: undefined },
        { field: "category", operator: "eq", value: undefined },
        { field: "is_active", operator: "eq", value: undefined },
      ],
    },
    syncWithLocation: true,
  });

  const hookCurrentPage = (tableProps.pagination as any)?.current ?? 1;
  const setCurrentPage = (page: number) => {
    if (tableProps.pagination && typeof (tableProps.pagination as any).onChange === 'function') {
      (tableProps.pagination as any).onChange(page, (tableProps.pagination as any).pageSize || 20);
    }
  };

  const { mutate: deleteTemplate } = useDelete();

  // Draft states for filters
  const [searchDraft, setSearchDraft] = useState("");
  const [categoryDraft, setCategoryDraft] = useState<string | undefined>();
  const [statusDraft, setStatusDraft] = useState<boolean | undefined>();

  // Applied filter values
  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("name", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const appliedCategoryValue = useMemo(() => {
    const f = getDefaultFilter("category", filters);
    return (f?.value as string | undefined) ?? undefined;
  }, [filters]);

  const appliedStatusValue = useMemo(() => {
    const f = getDefaultFilter("is_active", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  // Sync draft states with applied filters
  useEffect(() => {
    setSearchDraft(appliedSearchValue);
  }, [appliedSearchValue]);

  useEffect(() => {
    setCategoryDraft(appliedCategoryValue);
  }, [appliedCategoryValue]);

  useEffect(() => {
    setStatusDraft(appliedStatusValue);
  }, [appliedStatusValue]);

  // Unified filter update with page reset
  const updateFilters = useCallback(
    (search: string, category?: string, status?: boolean) => {
      const trimmed = search.trim();
      setFilters(
        [
          { field: "name", operator: "contains", value: trimmed || undefined },
          { field: "category", operator: "eq", value: category || undefined },
          { field: "is_active", operator: "eq", value: status !== undefined ? status : undefined },
        ],
        "replace"
      );
      if ((hookCurrentPage ?? 1) !== 1) {
        setCurrentPage(1);
      }
    },
    [hookCurrentPage, setCurrentPage, setFilters]
  );

  // Smart deletion with pagination
  const handleDelete = useCallback(
    (id: string, name: string) => {
      deleteTemplate(
        {
          resource: "templates",
          id,
          successNotification: () => ({
            message: `Шаблон "${name}" удален`,
            description: "",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            const dataSource = (tableQuery?.data?.data ?? []) as Template[];
            const isLastItemOnPage = dataSource.length <= 1;
            const pagination = tableProps.pagination === false ? undefined : tableProps.pagination;
            const currentPage = pagination?.current ?? hookCurrentPage ?? 1;

            if (currentPage > 1 && isLastItemOnPage) {
              setCurrentPage(currentPage - 1);
            } else {
              tableQuery.refetch();
            }
          },
        }
      );
    },
    [deleteTemplate, hookCurrentPage, setCurrentPage, tableProps.pagination, tableQuery]
  );


  const renderCategory = (category: string) => {
    const config = categoryMap[category] || { label: category, color: "default" };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const columns: ColumnsType<Template> = [
    {
      title: "Дата создания",
      dataIndex: "created_at",
      width: 120,
      sorter: true,
      defaultSortOrder: "descend",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      align: "center"
    },
    {
      title: "Название",
      dataIndex: "name",
      width: 250,
      ellipsis: true,
      filteredValue: appliedSearchValue ? [appliedSearchValue] : null,
      filterIcon: <SearchOutlined style={{ color: appliedSearchValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            allowClear
            placeholder="Поиск по названию"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onPressEnter={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}
            style={{ marginBottom: 8 }}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setSearchDraft("");
              updateFilters("", categoryDraft, statusDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: "Категория",
      dataIndex: "category",
      width: 150,
      filteredValue: appliedCategoryValue ? [appliedCategoryValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedCategoryValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 200 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Выберите категорию"
            style={{ width: '100%', marginBottom: 8 }}
            value={categoryDraft}
            onChange={(value) => setCategoryDraft(value)}
            options={categoryOptions}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setCategoryDraft(undefined);
              updateFilters(searchDraft, undefined, statusDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: renderCategory,
    },
    {
      title: "Статус",
      dataIndex: "is_active",
      width: 100,
      align: "center",
      filteredValue: appliedStatusValue !== undefined ? [appliedStatusValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedStatusValue !== undefined ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 180 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: '100%', marginBottom: 8 }}
            value={statusDraft}
            onChange={(value) => setStatusDraft(value)}
            options={[
              { value: true, label: "Активно" },
              { value: false, label: "Неактивно" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, categoryDraft, statusDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setStatusDraft(undefined);
              updateFilters(searchDraft, categoryDraft, undefined);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (active: boolean) =>
        active ? (
          <Tag color="success">Активно</Tag>
        ) : (
          <Tag color="default">Неактивно</Tag>
        ),
    },
    {
      title: "Описание",
      dataIndex: "description",
      ellipsis: {
        showTitle: false,
      },
      render: (desc) => (
        <Tooltip placement="topLeft" title={desc}>
          <Text ellipsis>{desc}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                go({
                  to: { resource: "templates", action: "edit", id: record.id },
                  type: "push",
                });
              }}
              aria-label="Редактировать"
            />
          </Tooltip>
          <Popconfirm
            title="Удалить шаблон"
            description={`Вы уверены, что хотите удалить "${record.name}"?`}
            onConfirm={() => handleDelete(record.id, record.name)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Удалить"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="Шаблоны ответов"
        headerButtons={() => (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              go({
                to: {
                  resource: "templates",
                  action: "create",
                },
                type: "push",
              })
            }
          >
            Создать шаблон
          </Button>
        )}
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          bordered
          size="middle"
          scroll={{ x: 1400 }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} шаблонов`,
          }}
        />
      </List>
    </>
  );
}
